import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { formatSats, formatTimestamp, formatRelativeTime, truncateKey } from '@/lib/formatters';
import { useState, useMemo, useCallback } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card } from '../ui/card';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { getMempoolBaseUrl, calculateEffectiveAmount } from '@/lib/utils';
import { removeHyphens } from '@/lib/utils';
import type { AddressTransaction } from '@sparkscan/api-react-sdk-hooks';
import { Input } from '../ui/input';

// Create a type alias for better readability with token transaction support
export function HistoryListSkeleton() {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-6 w-48" />
        <div className="flex items-center space-x-2">
          <Skeleton className="h-9 w-40" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
      <Card className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Skeleton className="h-5 w-20" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-5 w-24" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-5 w-24" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-5 w-24" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-5 w-24" />
              </TableHead>
              <TableHead>
                <Skeleton className="h-5 w-24" />
              </TableHead>
            </TableRow>
          </TableHeader>
        </Table>
      </Card>
    </div>
  );
}

interface HistoryListProps {
  transfers: AddressTransaction[];
  currentUserPubkey: string;
  sparkAddress: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  isLoading: boolean;
  totalTransfers: number;
}

type SortOption = {
  label: string;
  value: 'timestamp_desc' | 'timestamp_asc' | 'amount_desc' | 'amount_asc';
};

const sortOptions: SortOption[] = [
  { label: 'Newest First', value: 'timestamp_desc' },
  { label: 'Oldest First', value: 'timestamp_asc' },
  { label: 'Amount (High to Low)', value: 'amount_desc' },
  { label: 'Amount (Low to High)', value: 'amount_asc' },
];

export function HistoryList({
  transfers,
  currentUserPubkey,
  sparkAddress,
  currentPage,
  onPageChange,
  isLoading,
  totalTransfers,
  network = 'signet',
}: HistoryListProps & { network?: string }) {
  const ITEMS_PER_PAGE = 25;
  const [selectedSort, setSelectedSort] = useState<SortOption['value']>('timestamp_desc');
  const [jumpToPageInput, setJumpToPageInput] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Sorting helpers
  const getAmount = useCallback((tx: AddressTransaction): number => {
    if (
      tx.type === 'token_transfer' ||
      tx.type === 'token_multi_transfer' ||
      tx.type === 'token_mint' ||
      tx.type === 'token_burn'
    ) {
      return tx.tokenAmount || 0;
    }
    if (typeof tx.amountSats === 'number') return tx.amountSats;
    return 0;
  }, []);

  const sortedTransfers = useMemo(() => {
    if (isLoading || !transfers.length) return transfers;
    return [...transfers].sort((a, b) => {
      switch (selectedSort) {
        case 'timestamp_desc':
          return new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime();
        case 'timestamp_asc':
          return new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime();
        case 'amount_desc':
          return getAmount(b) - getAmount(a);
        case 'amount_asc':
          return getAmount(a) - getAmount(b);
        default:
          return 0;
      }
    });
  }, [transfers, getAmount, selectedSort, isLoading]);

  // Pagination helpers
  const totalPages = Math.ceil(totalTransfers / ITEMS_PER_PAGE) || 1;
  const paginatedTransfers = useMemo(() => {
    return sortedTransfers;
  }, [sortedTransfers]);

  // Helper: display address with correct formatting
  function renderAddress(
    info: AddressTransaction['counterparty'] | undefined,
    typeOverride?: 'spark' | 'bitcoin' | 'lightning' | 'system' | 'token',
    displayOverride?: string
  ) {
    const type = typeOverride || info?.type || 'unknown';
    const display = displayOverride || info?.identifier || (info?.pubkey ?? '');
    const isCurrentUser = display === sparkAddress || display === currentUserPubkey;

    if (type === 'bitcoin') {
      const mempoolUrl = `${getMempoolBaseUrl(network)}/address/${display}`;
      return (
        <a
          href={mempoolUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-mono text-orange-500/80 hover:underline"
        >
          {truncateKey(display, 6, 6)}
        </a>
      );
    }
    if (type === 'spark') {
      return (
        <Link
          href={`/address/${display}`}
          className={`font-mono ${
            isCurrentUser ? 'text-primary/50 font-medium' : 'text-primary hover:underline'
          }`}
        >
          {truncateKey(display, 6, 6)}
        </Link>
      );
    }
    if (type === 'lightning') {
      return <span className="font-mono">Lightning</span>;
    }
    if (type === 'token') {
      if (display === 'Issuer') {
        return <span className="font-mono text-white">Token Issuer</span>;
      }
      if (display === 'Burn Address') {
        return (
          <Link
            href={'/address/020202020202020202020202020202020202020202020202020202020202020202'}
            className="font-mono text-red-500 hover:underline"
          >
            Burn Address
          </Link>
        );
      }
      return <span className="font-mono text-white">{truncateKey(display, 6, 6)}</span>;
    }
    return <span className="font-mono text-muted-foreground">Unknown</span>;
  }

  // Format token amount based on decimals
  const formatTokenAmount = (amount: number, decimalsInput?: number, ticker = ''): string => {
    const decimals = decimalsInput ?? 8;

    const divisor = decimals === 0 ? 1 : 10 ** decimals;

    const formattedAmount = (amount / divisor).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    });
    return `${formattedAmount}${ticker ? ` ${ticker.toUpperCase()}` : ''}`;
  };

  // Table rendering helpers
  const renderAmount = (tx: AddressTransaction) => {
    let isOutgoing = tx.direction === 'outgoing' || tx.direction === 'destruction';

    // Handle token transactions
    if (
      tx.type === 'token_transfer' ||
      tx.type === 'token_multi_transfer' ||
      tx.type === 'token_mint' ||
      tx.type === 'token_burn'
    ) {
      const ticker = tx.tokenMetadata?.ticker || '';

      // Special cases for mint/burn
      if (tx.type === 'token_mint') {
        return (
          <span className="font-medium text-green-600">
            Mint {formatTokenAmount(tx.tokenAmount || 0, tx.tokenMetadata?.decimals, ticker)}
          </span>
        );
      }

      if (tx.type === 'token_burn') {
        return (
          <span className="font-medium text-red-600">
            Burn {formatTokenAmount(tx.tokenAmount || 0, tx.tokenMetadata?.decimals, ticker)}
          </span>
        );
      }

      // Multi-output transactions - check if current user is sender
      if (tx.type === 'token_multi_transfer') {
        let displayAmount = tx.tokenAmount || 0;

        // Calculate effective amount for multi-output transactions
        if ('multiIoDetails' in tx && tx.multiIoDetails) {
          const effectiveAmount = calculateEffectiveAmount(tx.multiIoDetails);
          if (effectiveAmount > 0) {
            displayAmount = effectiveAmount;
          }

          // Check if current user is in the inputs (sender)
          const multiIoDetails = tx.multiIoDetails;
          if (multiIoDetails.inputs && multiIoDetails.inputs.length > 0) {
            const isSender = multiIoDetails.inputs.some(
              (input: any) => input.address === sparkAddress || input.address === currentUserPubkey
            );
            if (isSender) {
              isOutgoing = true;
            }
          }
        }

        const sign = isOutgoing ? '-' : '+';
        return (
          <span className={`font-medium ${isOutgoing ? 'text-red-600' : 'text-green-600'}`}>
            {sign} {formatTokenAmount(displayAmount, tx.tokenMetadata?.decimals, ticker)}
          </span>
        );
      }

      // For regular token transfers, also check the getFromTo logic
      // to determine if current address is the sender
      const { from } = getFromTo(tx);
      // Check if the "from" contains our current address
      if (from && typeof from === 'object' && 'props' in from) {
        const fromProps = from.props;
        if (
          fromProps.children &&
          (fromProps.children === truncateKey(sparkAddress, 6, 6) ||
            fromProps.href === `/address/${sparkAddress}`)
        ) {
          isOutgoing = true;
        }
      }

      const sign = isOutgoing ? '-' : '+';
      return (
        <span className={`font-medium ${isOutgoing ? 'text-red-600' : 'text-green-600'}`}>
          {sign} {formatTokenAmount(tx.tokenAmount || 0, tx.tokenMetadata?.decimals, ticker)}
        </span>
      );
    }

    // Regular BTC transactions
    const ticker = 'BTC';
    const sign = isOutgoing ? '-' : '+';
    return (
      <span className={`font-medium ${isOutgoing ? 'text-red-600' : 'text-green-600'}`}>
        {sign} {formatSats(getAmount(tx))} {ticker}
      </span>
    );
  };

  // Table TxId (with fallback)
  const renderTxId = (tx: AddressTransaction) => {
    const txId = removeHyphens(tx.id);
    return (
      <Link href={`/tx/${txId}`} className="font-mono text-primary hover:underline">
        {truncateKey(txId, 6, 6)}
      </Link>
    );
  };

  // Pagination display
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endIdx = Math.min(currentPage * ITEMS_PER_PAGE, totalTransfers);

  // Determine "from" and "to" columns based on direction and counterparty
  function getFromTo(tx: AddressTransaction) {
    console.log('Processing transaction:', {
      id: tx.id,
      type: tx.type,
      direction: tx.direction,
      hasCounterparty: !!tx.counterparty,
      counterpartyType: tx.counterparty?.type,
      hasMultiIoDetails: 'multiIoDetails' in tx,
      multiIoDetails: 'multiIoDetails' in tx ? tx.multiIoDetails : undefined,
    });

    // Multi-output token transactions
    if (tx.type === 'token_multi_transfer') {
      // Check if multiIoDetails exists on the transaction
      const multiIoDetails = 'multiIoDetails' in tx ? tx.multiIoDetails : null;

      console.log('Multi-output transaction details:', {
        hasMultiIoDetails: !!multiIoDetails,
        inputsLength: multiIoDetails?.inputs?.length,
        outputsLength: multiIoDetails?.outputs?.length,
      });

      // Use the new detailed structure if available
      if (
        multiIoDetails &&
        multiIoDetails.inputs &&
        multiIoDetails.outputs &&
        multiIoDetails.inputs.length > 0 &&
        multiIoDetails.outputs.length > 0
      ) {
        // Get unique addresses
        const uniqueSenders = [
          ...new Set(multiIoDetails.inputs.map((input: any) => input.address)),
        ];
        const uniqueReceivers = [
          ...new Set(multiIoDetails.outputs.map((output: any) => output.address)),
        ];

        // Filter out change outputs (outputs that go back to one of the senders)
        const actualReceivers = uniqueReceivers.filter((addr) => !uniqueSenders.includes(addr));

        const hasMultipleSenders = uniqueSenders.length > 1;
        const hasMultipleReceivers = actualReceivers.length > 1;

        // Use direction to determine the user's role
        if (tx.direction === 'incoming') {
          return {
            from: hasMultipleSenders ? (
              <span className="font-mono text-muted-foreground">
                {uniqueSenders.length} Senders
              </span>
            ) : uniqueSenders[0] ? (
              renderAddress(undefined, 'spark', uniqueSenders[0])
            ) : (
              renderAddress(tx.counterparty)
            ),
            to: renderAddress(undefined, 'spark', sparkAddress),
          };
        } else if (tx.direction === 'outgoing') {
          return {
            from: renderAddress(undefined, 'spark', sparkAddress),
            to: hasMultipleReceivers ? (
              <span className="font-mono text-muted-foreground">
                {actualReceivers.length} Recipients
              </span>
            ) : actualReceivers[0] ? (
              renderAddress(undefined, 'spark', actualReceivers[0])
            ) : (
              renderAddress(tx.counterparty)
            ),
          };
        } else {
          // For "transfer" direction or others, determine based on whether current address is in inputs/outputs
          const isCurrentUserSender =
            uniqueSenders.includes(sparkAddress) || uniqueSenders.includes(currentUserPubkey);
          const isCurrentUserReceiver =
            uniqueReceivers.includes(sparkAddress) || uniqueReceivers.includes(currentUserPubkey);

          console.log('Transfer direction logic:', {
            isCurrentUserSender,
            isCurrentUserReceiver,
            sparkAddress,
            currentUserPubkey,
            uniqueSenders,
            uniqueReceivers,
          });

          if (isCurrentUserSender && !isCurrentUserReceiver) {
            // User is only a sender
            return {
              from: renderAddress(undefined, 'spark', sparkAddress),
              to: hasMultipleReceivers ? (
                <span className="font-mono text-muted-foreground">
                  {actualReceivers.length} Recipients
                </span>
              ) : actualReceivers[0] ? (
                renderAddress(undefined, 'spark', actualReceivers[0])
              ) : (
                <span className="font-mono text-muted-foreground">Self</span>
              ),
            };
          } else if (!isCurrentUserSender && isCurrentUserReceiver) {
            // User is only a receiver
            return {
              from: hasMultipleSenders ? (
                <span className="font-mono text-muted-foreground">
                  {uniqueSenders.length} Senders
                </span>
              ) : uniqueSenders[0] ? (
                renderAddress(undefined, 'spark', uniqueSenders[0])
              ) : (
                <span className="font-mono text-muted-foreground">Unknown</span>
              ),
              to: renderAddress(undefined, 'spark', sparkAddress),
            };
          } else {
            // User is both sender and receiver, or neither - show the actual parties
            return {
              from: hasMultipleSenders ? (
                <span className="font-mono text-muted-foreground">
                  {uniqueSenders.length} Senders
                </span>
              ) : uniqueSenders[0] ? (
                renderAddress(undefined, 'spark', uniqueSenders[0])
              ) : (
                <span className="font-mono text-muted-foreground">Unknown</span>
              ),
              to: hasMultipleReceivers ? (
                <span className="font-mono text-muted-foreground">
                  {actualReceivers.length} Recipients
                </span>
              ) : actualReceivers[0] ? (
                renderAddress(undefined, 'spark', actualReceivers[0])
              ) : uniqueSenders.length === 1 && actualReceivers.length === 0 ? (
                renderAddress(undefined, 'spark', uniqueSenders[0])
              ) : (
                <span className="font-mono text-muted-foreground">Unknown</span>
              ),
            };
          }
        }
      }

      // Fallback - use counterparty and direction to determine from/to
      console.log(tx, 'tx');
      console.log('Falling back to counterparty logic:', {
        hasCounterparty: !!tx.counterparty,
        direction: tx.direction,
        counterpartyDetails: tx.counterparty,
      });

      if (tx.counterparty) {
        if (tx.direction === 'incoming') {
          return {
            from: renderAddress(tx.counterparty),
            to: renderAddress(undefined, 'spark', sparkAddress),
          };
        } else if (tx.direction === 'outgoing') {
          return {
            from: renderAddress(undefined, 'spark', sparkAddress),
            to: renderAddress(tx.counterparty),
          };
        } else if (tx.direction === 'transfer') {
          // For transfer direction without multiIoDetails, show counterparty on both sides
          // This handles the edge case where we don't have detailed info
          return {
            from: renderAddress(tx.counterparty),
            to: renderAddress(tx.counterparty),
          };
        }
      }

      console.log('Returning Unknown for transaction:', tx.id);
      return {
        from: <span className="font-mono text-muted-foreground">Unknown</span>,
        to: <span className="font-mono text-muted-foreground">Unknown</span>,
      };
    }

    // Token transactions
    if (tx.type === 'token_mint') {
      return {
        from: renderAddress({ type: 'token', identifier: 'Issuer' }, 'token', 'Issuer'),
        to: renderAddress(undefined, 'spark', sparkAddress),
      };
    }

    if (tx.type === 'token_burn') {
      return {
        from: renderAddress(undefined, 'spark', sparkAddress),
        to: renderAddress(tx.counterparty, 'token'),
      };
    }

    if (tx.type === 'token_transfer') {
      if (tx.direction === 'incoming') {
        return {
          from: renderAddress(tx.counterparty),
          to: renderAddress(undefined, 'spark', sparkAddress),
        };
      }
      if (tx.direction === 'outgoing') {
        return {
          from: renderAddress(undefined, 'spark', sparkAddress),
          to: renderAddress(tx.counterparty),
        };
      }
    }

    // Lightning payments
    if (tx.type === 'lightning_payment') {
      if (tx.direction === 'outgoing') {
        return {
          from: renderAddress(undefined, 'spark', sparkAddress),
          to: <span className="font-mono">Lightning Invoice</span>,
        };
      }
      if (tx.direction === 'incoming') {
        return {
          from: <span className="font-mono">Lightning</span>,
          to: renderAddress(undefined, 'spark', sparkAddress),
        };
      }
    }

    // Bitcoin/Spark transfers
    if (tx.direction === 'incoming') {
      return {
        from: renderAddress(tx.counterparty),
        to: renderAddress(undefined, 'spark', sparkAddress),
      };
    }
    if (tx.direction === 'outgoing') {
      return {
        from: renderAddress(undefined, 'spark', sparkAddress),
        to: renderAddress(tx.counterparty),
      };
    }

    // fallback
    return {
      from: <span className="font-mono text-muted-foreground">Unknown</span>,
      to: <span className="font-mono text-muted-foreground">Unknown</span>,
    };
  }

  // Handle jump to page submission
  const handleJumpToPage = (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault(); // Prevent form submission if used
    const pageNum = Number.parseInt(jumpToPageInput, 10);
    if (
      !Number.isNaN(pageNum) &&
      pageNum >= 1 &&
      pageNum <= totalPages &&
      pageNum !== currentPage
    ) {
      onPageChange(pageNum);
      setJumpToPageInput(''); // Clear input after jump
      setIsDropdownOpen(false); // Close dropdown
    } else {
      // Optionally provide feedback for invalid input
      console.warn('Invalid page number entered:', jumpToPageInput);
      setJumpToPageInput(''); // Clear invalid input
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Transaction History</h3>
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Sort by <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => setSelectedSort(option.value)}
                  className={selectedSort === option.value ? 'bg-accent' : ''}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <Card className="p-0">
        {paginatedTransfers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tx Id</TableHead>
                <TableHead>From</TableHead>
                <TableHead>To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Age</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }, (_, i) => (
                    <TableRow key={`loading-${i + Math.random()}`}>
                      <TableCell>
                        <Skeleton className="h-4 w-24" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-24 ml-auto" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-36 ml-auto" />
                      </TableCell>
                    </TableRow>
                  ))
                : paginatedTransfers.map((tx) => {
                    const { from, to } = getFromTo(tx);
                    return (
                      <TableRow key={tx.id}>
                        <TableCell>{renderTxId(tx)}</TableCell>
                        <TableCell>{from}</TableCell>
                        <TableCell>{to}</TableCell>
                        <TableCell>
                          <span
                            className={
                              tx.status === 'failed'
                                ? 'text-destructive'
                                : tx.status !== 'confirmed'
                                  ? 'text-muted-foreground'
                                  : ''
                            }
                          >
                            {tx.status.charAt(0).toUpperCase() + tx.status.slice(1).toLowerCase()}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{renderAmount(tx)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap font-mono text-muted-foreground">
                          <span title={formatTimestamp(tx.createdAt || '')}>
                            {formatRelativeTime(tx.createdAt || '')}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            No transactions found for this account.
          </div>
        )}
        {/* Pagination Controls */}
        {totalTransfers > 0 && (
          <div className="flex justify-between items-center pb-6 px-2">
            <div className="text-sm text-muted-foreground">
              Showing {startIdx} to {endIdx} of {totalTransfers.toLocaleString()} transactions
            </div>
            <div className="flex space-x-2 items-center">
              <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage <= 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              {/* Page Selector Dropdown with Input */}
              {totalPages > 1 && (
                <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger asChild disabled={isLoading}>
                    <Button variant="outline" size="sm" className="h-9 w-auto px-3">
                      Page {currentPage} of {totalPages}
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="p-2">
                    <form onSubmit={handleJumpToPage} className="flex items-center space-x-1">
                      <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={jumpToPageInput}
                        onChange={(e) => setJumpToPageInput(e.target.value)}
                        placeholder={`1-${totalPages}`}
                        className="h-8 w-20 px-2 text-sm text-center"
                        // Auto focus when dropdown opens
                        autoFocus
                        onKeyDown={(e) => {
                          // Allow submitting with Enter key
                          if (e.key === 'Enter') {
                            handleJumpToPage();
                          }
                        }}
                      />
                      <Button
                        type="submit"
                        variant="outline"
                        size="sm"
                        className="h-8 px-2"
                        disabled={
                          isLoading ||
                          !jumpToPageInput ||
                          Number.parseInt(jumpToPageInput, 10) === currentPage ||
                          Number.parseInt(jumpToPageInput, 10) > totalPages ||
                          Number.parseInt(jumpToPageInput, 10) < 1
                        }
                      >
                        Go
                      </Button>
                    </form>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {/* End Page Selector Dropdown with Input */}

              <Button
                variant="outline"
                size="icon"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages || isLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
